"use client";

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";
import { CustomerQueryParams } from "@/types/customer.api.types";

export interface CustomerFilters {
  search: string;
  customerType: string;
  customerCategory: string;
  customerStatus: string;
}

interface CustomerFilterProps {
  filters: CustomerFilters;
  onFilterChange: (filters: CustomerFilters) => void;
}

export function CustomerFilter({
  filters,
  onFilterChange,
}: CustomerFilterProps) {
  const handleFilterChange = (key: keyof CustomerFilters, value: string) => {
    const newFilters = { ...filters, [key]: value };
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const clearedFilters: CustomerFilters = {
      search: "",
      customerType: "all",
      customerCategory: "all",
      customerStatus: "all",
    };
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters =
    filters.search !== "" ||
    filters.customerType !== "all" ||
    filters.customerCategory !== "all" ||
    filters.customerStatus !== "all";

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Filters</h3>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="h-8"
              >
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <Label>Search</Label>
              <Input
                placeholder="Search by name, email, or phone..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>

            {/* Customer Category Filter */}
            <div>
              <Label>Category</Label>
              <Select
                value={filters.customerCategory}
                onValueChange={(value) => handleFilterChange("customerCategory", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="regular-customer">Regular</SelectItem>
                  <SelectItem value="occasional-customer">Occasional</SelectItem>
                  <SelectItem value="vip-customer">VIP</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Customer Status Filter */}
            <div>
              <Label>Status</Label>
              <Select
                value={filters.customerStatus}
                onValueChange={(value) => handleFilterChange("customerStatus", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

